import { FC, useEffect, useMemo, useCallback } from "react"

import { Modal, Button } from "react-materialize"

import { useAppDispatch, useAppSelector } from "@/app/hooks"

import {
  setPropzMediaStateParameter,
} from "@/features/propzMedia/propzMediaSlice"

import { format } from 'date-fns'
import { parseDate } from '@assets/utils'

import './style.sass'

import { formatPriceToBr } from "@/utils"

import { formatPrice } from '@assets/utils/formatPrice'

import { ReactComponent as ArrowDownSVG } from './ArrowDown.svg'
import { ReactComponent as CancelSVG } from './Cancel.svg'
import { ReactComponent as DoneSVG } from './Done.svg'
import { ReactComponent as ExclamationSVG } from './Exclamation.svg'
import { ReactComponent as WatchSVG } from './Watch.svg'

/*
To show cashback operations
*/
export const CashbackModal: FC = () => {

  const dispatch = useAppDispatch()

  const {
    showCashbackModal,
    transactions
  } = useAppSelector(
    (state) => state.propzMedia
  )

  const statements: TransactionCreditStatement[] = useMemo(() => {
    const final: TransactionCreditStatement[] = [];
    try {
      (transactions || []).forEach((transaction: any) => {
        let remaining: number = transaction.amount;
        if (typeof remaining != 'number' || Number.isNaN(remaining) || remaining < 0.01) {
          return
        }
        let isPending: boolean = false;
        let isExpired: boolean = false;
        let isCancelled: boolean = false;
        if (transaction.amount >= 0.01) {
          if (transaction.creditType == "EXTERNAL") {
            isPending = transaction.creditOperation?.commissionStatus?.toLowerCase() == "pending";
            isExpired = transaction.creditOperation?.commissionStatus?.toLowerCase() == "approved"
                && (
                    new Date(transaction.expirationCreditDate).getTime() < Date.now()
                    || transaction.creditRedeems?.some((creditRedeem: {
                      debitType: string
                    }) => creditRedeem.debitType == "EXPIRATION")
                )
            isCancelled = transaction.creditOperation?.commissionStatus?.toLowerCase() == "declined"
                || transaction.creditRedeems?.some((creditRedeem: {
                  debitType: string
                }) => creditRedeem.debitType == "CANCELLATION")
          } else if (transaction.creditType == "PROMOTION") {
            isPending = transaction.creditOperation?.creditStatus?.toLowerCase() != "approved";
            isExpired = transaction.creditRedeems?.some((creditRedeem: {
              debitType: string
            }) => creditRedeem.debitType == "EXPIRATION");
            isCancelled = transaction.creditRedeems?.some((creditRedeem: {
              debitType: string
            }) => creditRedeem.debitType == "CANCELLATION");
          } else {
            console.log("Error: understanding transaction with unexpected credit type: " + JSON.stringify(transaction))
            return
          }
        } else {
          console.log("Error: transaction with invalid amount: " + JSON.stringify(transaction))
          return
        }
        const creditRedeems: TransactionDebitStatement[] = [];
        transaction.creditRedeems?.forEach((creditRedeem: any) => {
          if (creditRedeem.amount >= 0.01) {
            remaining -= creditRedeem.amount
            creditRedeems.push(
                new TransactionDebitStatement(
                    creditRedeem.amount,
                    creditRedeem.dateCreated,
                    creditRedeem.debitType
                )
            );
          } else {
            console.log("Error: creditRedeems without valid amount: " + JSON.stringify(creditRedeem))
            return
          }
        })
        if (remaining <= -0.01) {
          console.log("Error: remaining (" + remaining + ") for: " + JSON.stringify(transaction))
        }
        final.push(
            new TransactionCreditStatement(
                transaction.creditDateCreated,
                transaction.amount,
                remaining,
                isPending,
                isExpired,
                isCancelled,
                creditRedeems
            )
        )
      })
      return final
    } catch (e) {
      console.log('Error: transactions statements transactions : ', transactions)
      console.log('Error: transactions statements error : ', e)
      return []
    }
  }, [transactions])

  const transactionStatus = useCallback((transaction: TransactionCreditStatement) => {
    if (transaction.isPending) {
      return (<><ExclamationSVG/> A receber </>)
    }
    if (transaction.isExpired) {
      return (<><WatchSVG/> Expirado </>)
    }
    if (transaction.isCancelled) {
      return (<><CancelSVG/> Cancelado </>)
    }
    if (transaction.isUsed) {
      return (<><ArrowDownSVG/> Utilizado </>)
    } else {
      return (<><DoneSVG/> Recebido </>)
    }
  }, [])

  const displayAvailable = useCallback((transaction: TransactionCreditStatement) => {
    return (
        <>
          {transaction.hasUsableRemaining() && (
              <div className="line">
                Disponível: <span
                  className="amount">{formatPrice(transaction.remaining)}</span>
              </div>
          )}
        </>
    );
  }, [])

  const displayDebits = useCallback((transaction: TransactionCreditStatement) => {
    return (
        <>
          {transaction.creditRedeems && transaction.creditRedeems.length > 0 &&
              transaction.creditRedeems.map((debit: TransactionDebitStatement, index) => {
                let debitAmount = formatPrice(debit.total)
                let description = "";
                if (debit.isUsage) {
                  description = " usado";
                } else if (debit.isExpired) {
                  description = " expirado";
                } else if (debit.isCancelled) {
                  description = " cancelado";
                }
                return (
                    <div className="line" key={index}>
                      &emsp;<span
                        className="amount">{debitAmount}</span>&nbsp;{description} {debit.when}
                    </div>
                );
              })
          }
        </>
    );
  })

  return (
    <Modal
      actions={[]}
      bottomSheet
      id="cashbackModal"
      open={showCashbackModal}
      options={{
        dismissible: true,
        endingTop: "10%",
        inDuration: 250,
        opacity: 0.5,
        outDuration: 250,
        preventScrolling: true,
        startingTop: "4%",
        onCloseEnd: () => dispatch(setPropzMediaStateParameter({ showCashbackModal: false })),
      }}
    >
      <h4 className="header">
        Meu extrato
        <Button
          flat
          waves="cyan"
          className="white stores-close-button"
          onClick={() => dispatch(setPropzMediaStateParameter({ showCashbackModal: false }))}
        >
          <i className="material-icons">close</i>
        </Button>
      </h4>

      <h5 className="saldo_atual">
        {'Saldo atual: '}
        <span>
          { formatPriceToBr(window.personalBalance) }
        </span>
      </h5>

      <ul className="collection" >
        {statements?.map((transaction: TransactionCreditStatement, i) => (
          <li key={`transaction-${i}`} className="collection-item">
            <div className="line">
              {'Cashback: '}
              <span className="amount">
                {/*<span className="money_sign">R$ </span>*/}
                {formatPrice(transaction.total)}
              </span>
              {transactionStatus(transaction)}
            </div>
            <div className="line">
              {'Adquirido'}
              {transaction.when}
            </div>
            {displayAvailable(transaction)}
            {displayDebits(transaction)}
          </li>
        ))}
      </ul>
    </Modal>
  );
};

class TransactionCreditStatement {
  public readonly when: string;
  public readonly total: number;
  public readonly remaining: number;
  public readonly isPending: boolean;
  public readonly isExpired: boolean;
  public readonly isCancelled: boolean;
  public readonly isUsed: boolean;
  public readonly creditRedeems: TransactionDebitStatement[];

  constructor(
      when: string,
      total: number,
      remaining: number,
      isPending: boolean,
      isExpired: boolean,
      isCancelled: boolean,
      creditRedeems: TransactionDebitStatement[]
  ) {
    this.when = when ? ' em ' + format(parseDate(when) as Date, 'dd/MM/yyyy') : '';
    this.total = total;
    this.remaining = remaining;
    this.isPending = isPending;
    this.isExpired = isExpired;
    this.isCancelled = isCancelled;
    this.creditRedeems = creditRedeems;
    this.isUsed = remaining < 0.01;
  }

  public hasUsableRemaining(): boolean {
    return !this.isPending && !this.isExpired && !this.isCancelled && !this.isUsed
  }
}

class TransactionDebitStatement {
  public readonly total: number;
  public readonly when: string;
  public readonly isUsage: boolean;
  public readonly isExpired: boolean;
  public readonly isCancelled: boolean;

  constructor(
      total: number,
      when: string,
      debitType: string
  ) {
    this.total = total;
    this.when = when ? ' em ' + format(parseDate(when) as Date, 'dd/MM/yyyy') : '';
    this.isUsage = debitType == "USAGE";
    this.isExpired = debitType == "EXPIRATION";
    this.isCancelled = debitType == "CANCELLATION";
  }
}